// ABA "NAO QUER RECEBER" — quem pediu pra sair das promocoes (RDS chamados 1.29 / DA-712).
//
// Pedido dele em 10/09/2026, desenhando o disparo de fly por etiqueta: *"Se o contato responder
// 'Nao quero', o sistema incluiria ele numa nova aba no Gestao de Vendas 'Nao Quer Receber',
// todos os numeros que forem para esta lista, nao recebera mais nenhum fly promocional nosso"*.
//
// ⚠️ NAO E' A MESMA COISA que "Nao sao venda" (o botao do cabecalho, [DA-685]). La o numero SOME
// da Gestao de Vendas inteira porque nao e' cliente. Aqui a pessoa E' cliente, continua em todas
// as abas e contadores, continua comprando e sendo atendida — so' nao recebe OFERTA. Os dois
// avisos na tela existem porque as duas listas parecem a mesma coisa pelo nome.
//
// ⛔ **Nasce PLACAR, nao fila** (mesmo raciocinio do `PlacarRecusas`): ninguem "trabalha" esta
// lista e nada esvazia. Ela serve pra CONFERIR — por que o botao de material apareceu desligado
// numa conversa, e quantos ja pediram pra sair.
//
// ⚠️ **Sem verde/vermelho** ([[user_daltonismo]]): quem pediu pelo WhatsApp vem em ambar
// `#b45309`, quem a loja incluiu na mao vem em cinza `#64748b` — a diferenca e' de TOM.
import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, UserMinus, Plus, UserRound } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { Avatar } from '../../components/Avatar';
import { BadgeEtiqueta } from '../atendimento/componentes/BadgeEtiqueta';
import { LightboxImagem } from '../atendimento/componentes/LightboxImagem';

interface Linha {
  id: number;
  numero: string;
  origem: string;
  motivo: string | null;
  ativo: boolean;
  criado_em: string;
  criado_por: string | null;
  nome_contato: string | null;
  /** Pedido dele em 22/09/2026 ([DA-737]) — a lista dizia o nome e nao dizia quem e' a pessoa. */
  foto_url: string | null;
  /**
   * Juntas de TODAS as fichas do mesmo numero, nao so' da que deu o nome. `null` quando o
   * numero nao tem ficha nenhuma — que e' o caso de quem a loja incluiu na mao.
   *
   * ⛔ Com COR e APELIDO: o contexto de etiquetas so' e' preenchido por quem abre o modal
   * "Gerenciar etiquetas", entao confiar nele deixava todo selo cinza aqui ([DA-737]).
   */
  etiquetas: Array<{ nome: string; cor: string | null; apelido: string | null }> | null;
  /**
   * O cliente do cadastro casado por telefone. ⚠️ NAO e' etiqueta — o chat desenha os dois lado
   * a lado e isso confundiu na tela ([DA-737]); aqui vao em colunas separadas de proposito.
   */
  cliente: { nome: string; codigo: string | null } | null;
}

function dataCurta(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/** `556492326324` -> `(64) 9232-6324`. So pra ler; o que vale e' o numero cru. */
function telefoneBonito(bruto: string): string {
  const d = String(bruto || '').replace(/\D/g, '');
  const sem55 = d.startsWith('55') ? d.slice(2) : d;
  if (sem55.length < 10) return bruto;
  const ddd = sem55.slice(0, 2);
  const resto = sem55.slice(2);
  const meio = resto.length === 9 ? 5 : 4;
  return `(${ddd}) ${resto.slice(0, meio)}-${resto.slice(meio)}`;
}

export function PainelNaoQuerReceber({ ehAdmin }: { ehAdmin: boolean }) {
  const toast = useToast();
  const dialog = useDialog();
  const [linhas, setLinhas] = useState<Linha[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [novoNumero, setNovoNumero] = useState('');
  const [novoMotivo, setNovoMotivo] = useState('');
  const [gravando, setGravando] = useState(false);
  // A foto ampliada (pedido dele, 24/09/2026) — o MESMO lightbox do chat e da aba Contatos do
  // dia ([DA-701]): quem ja ampliou foto la sabe fechar, girar e baixar sem aprender nada novo.
  const [fotoAberta, setFotoAberta] = useState<{ url: string; nome: string } | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.get<{ linhas: Linha[] }>(
        `/api/nao-quer-receber${mostrarInativos ? '?todos=1' : ''}`,
      );
      setLinhas(r.linhas ?? []);
    } catch (e) {
      toast.erro((e as Error).message || 'Falha ao carregar a lista');
    } finally {
      setCarregando(false);
    }
  }, [mostrarInativos, toast]);

  useEffect(() => { void carregar(); }, [carregar]);

  const incluir = async () => {
    const so = novoNumero.replace(/\D/g, '');
    if (so.length < 10) {
      toast.erro('Informe o numero com DDD — ex.: (64) 99259-7498');
      return;
    }
    setGravando(true);
    try {
      await api.post('/api/nao-quer-receber', { numero: novoNumero, motivo: novoMotivo || undefined });
      setNovoNumero('');
      setNovoMotivo('');
      toast.sucesso('Numero incluido — nao recebe mais promocao nossa');
      await carregar();
    } catch (e) {
      toast.erro((e as Error).message || 'Falha ao incluir');
    } finally {
      setGravando(false);
    }
  };

  const voltarAReceber = async (l: Linha) => {
    const quem = l.nome_contato || telefoneBonito(l.numero);
    // O aviso de QUEM pediu vai na propria mensagem, e o caso do cliente e' marcado como
    // perigoso: voltar a mandar oferta pra quem pediu pra parar e' o erro caro deste painel.
    const ok = await dialog.confirmar(
      l.origem === 'cliente'
        ? `${quem} volta a receber nossas promocoes?\n\nFoi o PROPRIO CLIENTE que pediu pra sair, respondendo "Nao quero" no WhatsApp. So faca isso se ele pediu pra voltar.`
        : `${quem} volta a receber nossas promocoes?\n\nEste numero foi incluido na lista pela loja.`,
      { titulo: 'Tirar da lista', textoConfirmar: 'Voltar a receber', perigoso: l.origem === 'cliente' },
    );
    if (!ok) return;
    try {
      await api.put(`/api/nao-quer-receber/${l.id}`, { ativo: false });
      toast.sucesso(`${quem} volta a receber`);
      await carregar();
    } catch (e) {
      toast.erro((e as Error).message || 'Falha ao tirar da lista');
    }
  };

  const ativos = linhas.filter((l) => l.ativo);

  return (
    <div style={{ padding: '4px 2px' }}>
      {/* O aviso que separa as duas listas parecidas vem ANTES da lista, nao depois — quem abre a
          aba pela primeira vez precisa saber o que ela NAO faz. Molde do aviso da [DA-693] p.6. */}
      <div
        style={{
          background: '#fde68a', color: '#000', padding: '8px 12px',
          borderRadius: 6, fontSize: 13, marginBottom: 12, lineHeight: 1.5,
        }}
      >
        <strong>Estes clientes nao recebem mais fly promocional</strong> — nem o automatico, nem o
        que a vendedora manda no chat, nem disparo por etiqueta. Eles <strong>continuam clientes
        normais</strong>: aparecem nas outras abas, compram e sao atendidos como sempre. Para
        esconder quem <em>nao e cliente</em> (fornecedor, equipe), use o botao{' '}
        <strong>Nao sao venda</strong> no cabecalho.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600 }}>
          {carregando ? 'carregando...' : `${ativos.length} ${ativos.length === 1 ? 'numero' : 'numeros'} fora das promocoes`}
        </span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={mostrarInativos}
            onChange={(e) => setMostrarInativos(e.target.checked)}
          />
          Mostrar quem voltou a receber
        </label>
        <button type="button" className="btn-secundario" onClick={() => void carregar()} style={{ marginLeft: 'auto' }}>
          <RefreshCw size={14} /> Atualizar
        </button>
      </div>

      {/* Incluir na mao: o cliente costuma pedir pra sair FALANDO com a vendedora, nao escrevendo
          a frase exata. Sem esta porta o pedido morreria no atendimento. Aberto a qualquer perfil
          de proposito — errar pra mais aqui e' uma oferta a menos, e exigir admin seria o beco da
          [DA-697], onde a tela oferece o que o servidor recusa. */}
      <div
        style={{
          display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
          padding: '10px 12px', borderRadius: 6, marginBottom: 14,
          background: 'var(--panel-bg)', border: '1px solid var(--border-color, #cbd5e1)',
        }}
      >
        <input
          value={novoNumero}
          onChange={(e) => setNovoNumero(e.target.value)}
          placeholder="Telefone com DDD"
          style={{ width: 170 }}
        />
        <input
          value={novoMotivo}
          onChange={(e) => setNovoMotivo(e.target.value)}
          placeholder="Motivo (opcional) — ex.: pediu por telefone"
          style={{ flex: 1, minWidth: 220 }}
        />
        <button type="button" className="btn-primario" onClick={() => void incluir()} disabled={gravando}>
          <Plus size={14} /> {gravando ? 'Incluindo...' : 'Incluir na lista'}
        </button>
      </div>

      {!carregando && linhas.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
          Ninguem pediu pra sair das promocoes ainda.
          <div style={{ fontSize: 12, marginTop: 6 }}>
            Quando um cliente responder <strong>Nao quero</strong> no WhatsApp, ele aparece aqui sozinho.
          </div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="tabela-grid" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Contato</th>
                <th style={{ textAlign: 'left' }}>Cliente</th>
                <th style={{ textAlign: 'left' }}>Etiquetas</th>
                <th style={{ textAlign: 'left' }}>Telefone</th>
                <th style={{ textAlign: 'left' }}>Quem tirou</th>
                <th style={{ textAlign: 'left' }}>Motivo</th>
                <th style={{ textAlign: 'left' }}>Desde</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {linhas.map((l) => (
                <tr key={l.id} style={{ opacity: l.ativo ? 1 : 0.5 }}>
                  {/* Foto colada no nome, como em toda lista de contato da casa — a mesma
                      pergunta ("quem e' esta pessoa?") merece a mesma forma. */}
                  <td>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {/* Clicar amplia. So' com FOTO NA TELA: se a URL do WhatsApp expirou o
                          `Avatar` ja caiu nas iniciais e o clique morre junto ([DA-701]). */}
                      <Avatar
                        nome={l.nome_contato || '?'}
                        fotoUrl={l.foto_url}
                        tamanho={26}
                        onFotoClick={l.foto_url
                          ? () => setFotoAberta({ url: l.foto_url!, nome: l.nome_contato || telefoneBonito(l.numero) })
                          : undefined}
                      />
                      {l.nome_contato || <span style={{ color: 'var(--text-muted)' }}>sem cadastro</span>}
                    </span>
                  </td>
                  {/* O cliente do cadastro, em COLUNA PROPRIA. ⚠️ No chat ele aparece colado nas
                      etiquetas e foi lido como se fosse uma delas; aqui a separacao e' o ponto.
                      Ícone de pessoa e o mesmo formato `nome · #codigo` que o chat usa. */}
                  <td style={{ fontSize: 12 }}>
                    {l.cliente ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <UserRound size={12} style={{ flex: '0 0 auto' }} />
                        {l.cliente.nome}{l.cliente.codigo ? ` · #${l.cliente.codigo}` : ''}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>sem cadastro</span>
                    )}
                  </td>
                  {/* ⛔ Reusa o `BadgeEtiqueta`, que pega cor e apelido do contexto — repintar
                      etiqueta aqui faria o selo divergir do que as outras seis telas mostram.
                      ⚠️ EMPILHADAS, corte dele em 22/09/2026 ([DA-737]): lado a lado, duas
                      etiquetas compridas viram uma tira so' e o olho nao separa onde uma acaba.
                      `flex-start` pra cada selo ficar do tamanho do proprio texto. */}
                  <td>
                    {l.etiquetas?.length ? (
                      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3 }}>
                        {l.etiquetas.map((e) => (
                          <BadgeEtiqueta key={e.nome} nome={e.nome} cor={e.cor ?? undefined} apelido={e.apelido} />
                        ))}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{telefoneBonito(l.numero)}</td>
                  <td style={{ color: l.origem === 'cliente' ? '#b45309' : '#64748b', fontWeight: l.origem === 'cliente' ? 600 : 400 }}>
                    {l.origem === 'cliente' ? 'o proprio cliente' : `a loja${l.criado_por ? ` (${l.criado_por})` : ''}`}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{l.motivo || '—'}</td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{dataCurta(l.criado_em)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {!l.ativo ? (
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>voltou a receber</span>
                    ) : ehAdmin ? (
                      <button
                        type="button"
                        className="btn-secundario"
                        onClick={() => void voltarAReceber(l)}
                        title="Tirar da lista — volta a receber promocoes"
                      >
                        <UserMinus size={13} /> Voltar a receber
                      </button>
                    ) : (
                      // Botao que nao existe pra este perfil nao vira botao mudo: a linha DIZ
                      // por que ([DA-697]). Tirar alguem da lista e' o lado caro do erro —
                      // volta a mandar oferta pra quem pediu pra parar —, por isso so admin.
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>so o admin tira</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {fotoAberta && (
        <LightboxImagem
          imagens={[{ url: fotoAberta.url, legenda: fotoAberta.nome }]}
          onFechar={() => setFotoAberta(null)}
        />
      )}
    </div>
  );
}
